import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { FiCopy, FiEdit2, FiShare2, FiTrash2 } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Pagination } from "../Common";
import {
  postPartialPayment,
  patchPartialPayment,
  deletePartialPayment,
  suggestionSearch,
} from "../../services";

const paymentTypes = [
  { id: 1, label: "Partials" },
  { id: 2, label: "Fully" },
];

const statusOptions = [
  { id: 1, label: "Pending" },
  { id: 2, label: "Completed" },
  { id: 3, label: "Cancelled" },
];

const statusStyles = {
  Pending: "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-red-100 text-red-700",
};

const OffLineOrders = () => {
  const [orders, setOrders] = useState([]);
  const [currPage, setCurrPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [prevPage, setPrevPage] = useState(1);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentData, setPaymentData] = useState({
    others: "",
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    amount_paid: "",
    payment_type: 1,
    no_of_products: 1,
    status: 1,
    buyer: 0,
    product: 0,
    payment_date: "",
  });
  const [paging, setPaging] = useState({ total: 0, total_pages: 1 });
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [notification, setNotification] = useState("");
  const [userList, setUserList] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const userDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  // ----- API Calls (memoized) -----
  const fetchOrders = useCallback(
    async (page = 1, page_size = 5) => {
      setIsLoading(true);
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_BASEURL}/product/partial-payments/`,
          {
            params: { page, page_size },
            headers: {
              accept: "application/json",
              Authorization: `Token ${localStorage.getItem("adminAuth")}`,
            },
          }
        );

        const data = resp?.data;
        if (data) {
          if (Array.isArray(data.results)) {
            setOrders(data.results);
            setPaging({
              total: data.count || data.results.length,
              total_pages:
                data.total_pages ||
                Math.ceil((data.count || data.results.length) / page_size),
            });
            setCurrPage(data.current_page || page);
          } else if (Array.isArray(data)) {
            setOrders(data);
            setPaging({
              total: data.length,
              total_pages: Math.ceil(data.length / page_size),
            });
          } else {
            setOrders([]);
            setPaging({ total: 0, total_pages: 1 });
          }
        }
      } catch (error) {
        console.error("Error loading offline orders", error);
        setNotification("Failed to load orders. Please refresh.");
        toast.error("Failed to load orders");
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/all-user-list/`,
        {
          headers: {
            accept: "application/json",
            Authorization: `Token ${localStorage.getItem("adminAuth")}`,
          },
        }
      );
      setUserList(response?.data || []);
    } catch (error) {
      console.error("Error loading users for buyer search", error);
      toast.error("Failed to load user list");
    }
  }, []);

  const fetchProductSuggestions = useCallback(async (query) => {
    if (!query) {
      setProductSuggestions([]);
      return;
    }

    try {
      // Prefer improved generic_search endpoint
      const resp = await axios.get(`${process.env.REACT_APP_BASEURL}/product/improved-product/generic_search/`, {
        params: { search: query, isSearch: true },
        headers: {
          accept: "application/json",
          Authorization: `Token ${localStorage.getItem("adminAuth")}`,
        },
      });

      const data = resp?.data;
      if (data) {
        if (Array.isArray(data.results)) {
          setProductSuggestions(data.results);
          return;
        }
        if (Array.isArray(data)) {
          setProductSuggestions(data);
          return;
        }
        if (Array.isArray(data.products)) {
          setProductSuggestions(data.products);
          return;
        }
      }

      // fallback to existing suggestionSearch
      const result = await suggestionSearch(query);
      if (result && Array.isArray(result.suggestions)) {
        setProductSuggestions(result.suggestions);
      } else if (Array.isArray(result)) {
        setProductSuggestions(result);
      } else {
        // final fallback to product listing endpoint
        const fallback = await axios.get(`${process.env.REACT_APP_BASEURL}/product/product/`, {
          params: { q: query, page: 1, page_size: 20 },
          headers: { accept: "application/json" },
        });
        setProductSuggestions(fallback?.data?.results || []);
      }
    } catch (error) {
      console.error("Error fetching product suggestions", error);
      setProductSuggestions([]);
      toast.error("Failed to fetch products");
    }
  }, []);

  // ----- Effects -----
  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  useEffect(() => {
    fetchOrders(currPage, perPage);
  }, [currPage, perPage, fetchOrders]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (productQuery.trim()) {
        fetchProductSuggestions(productQuery.trim());
      } else {
        setProductSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [productQuery, fetchProductSuggestions]);

  // Click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ----- Derived / Memoized -----
  const filteredUsers = useMemo(() => {
    const query = paymentData.buyer_phone.toLowerCase().trim();
    if (!query) return [];
    return userList.filter(
      (user) =>
        user?.username?.toLowerCase().includes(query) ||
        user?.email?.toLowerCase().includes(query) ||
        user?.phone?.toLowerCase().includes(query) ||
        user?.mobile?.toLowerCase().includes(query)
    );
  }, [userList, paymentData.buyer_phone]);

  const getPaymentTypeLabel = (id) =>
    paymentTypes.find((type) => type.id === id)?.label || "Unknown";

  const getStatusLabel = (status, status_label) =>
    status_label ||
    (status === 2 ? "Completed" : status === 3 ? "Cancelled" : "Pending");

  // ----- Handlers (memoized) -----
  const openNewPaymentModal = useCallback(() => {
    setSelectedOrder(null);
    setSelectedBuyer(null);
    setSelectedProduct(null);
    setProductQuery("");
    setPaymentData({
      others: "",
      buyer_name: "",
      buyer_email: "",
      buyer_phone: "",
      amount_paid: "",
      payment_type: 1,
      no_of_products: 1,
      status: 1,
      buyer: 0,
      product: 0,
    });
    setShowPopup(true);
  }, []);

  const openPaymentModal = useCallback((order) => {
    setSelectedOrder(order);
    setSelectedBuyer(
      order.buyer
        ? {
          uid: order.buyer,
          username: order.buyer_name,
          email: order.buyer_email,
          phone: order.buyer_phone,
        }
        : null
    );
    const productId =
      order.product || order.product_details?.id || order.product_list?.[0]?.id || 0;
    const productName =
      order.product_details?.equip_name ||
      order.product_details?.name ||
      order.product_list?.find((item) => item.id === productId)?.name ||
      "";
    const notes =
      typeof order.others === "object"
        ? order.others?.notes || ""
        : order.others || "";
    setSelectedProduct(
      productId
        ? {
          id: productId,
          name: productName,
        }
        : null
    );
    setProductQuery(productName);
    // Extract only the date part (YYYY-MM-DD) so it binds correctly to <input type="date">
    const rawDate = order.payment_date || "";
    const paymentDateValue = rawDate ? rawDate.split("T")[0] : "";
    setPaymentData({
      others: notes,
      buyer_name: order.buyer_name || "",
      buyer_email: order.buyer_email || "",
      buyer_phone: order.buyer_phone || "",
      amount_paid: order.amount_paid || "",
      payment_type: order.payment_type || 1,
      no_of_products: order.no_of_products || 1,
      status: order.status || 1,
      buyer: order.buyer || 0,
      product: productId,
      payment_date: paymentDateValue,
    });
    setShowPopup(true);
  }, []);

  const closePopup = useCallback(() => {
    setShowPopup(false);
    setSelectedOrder(null);
  }, []);

  const updatePage = (e) => {
    let name = e.currentTarget.name;
    if (name === "prev") {
      setCurrPage(currPage === 1 ? 1 : currPage - 1);
      setPrevPage(currPage);
    } else if (name === "next") {
      setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
      setPrevPage(currPage);
    } else {
      setCurrPage(parseInt(e.currentTarget.innerHTML));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePaymentChange = useCallback((e) => {
    const { name, value } = e.target;
    setPaymentData((prev) => ({
      ...prev,
      [name]: ["no_of_products", "payment_type", "status", "buyer", "product"].includes(
        name
      )
        ? parseInt(value, 10) || 0
        : value,
    }));
    if (["buyer_name", "buyer_email", "buyer_phone"].includes(name)) {
      setSelectedBuyer(null);
    }
  }, []);

  const handleSelectBuyer = useCallback((user) => {
    console.log("selected user", user);
    const phone = user?.phone || user?.mobile || "";
    setSelectedBuyer(user);
    setPaymentData((prev) => ({
      ...prev,
      buyer_name: user.username || "",
      buyer_email: user.email || "",
      buyer_phone: phone,
      buyer: user.id || 0,
    }));
    setShowUserDropdown(false);
  }, []);

  const handleBuyerSearchInput = useCallback((value) => {
    setPaymentData((prev) => ({
      ...prev,
      buyer_phone: value,
    }));
    setShowUserDropdown(true);
    if (!value) {
      setSelectedBuyer(null);
      setPaymentData((prev) => ({ ...prev, buyer: 0 }));
    }
  }, []);

  const handleSelectProduct = useCallback((product) => {
    setSelectedProduct(product);
    setProductQuery(product.equip_name || product.name || "");
    setPaymentData((prev) => ({
      ...prev,
      product: product.id || product.product_id || 0,
    }));
    setShowProductDropdown(false);
  }, []);

  const handleProductInput = useCallback((value) => {
    setProductQuery(value);
    setShowProductDropdown(true);
    setSelectedProduct(null);
    setPaymentData((prev) => ({
      ...prev,
      product: 0,
    }));
  }, []);

  const submitVirtualPayment = useCallback(
    async (e) => {
      e.preventDefault();
      const payload = {
        others: paymentData.others ? { notes: paymentData.others.trim() } : {},
        buyer_name: paymentData.buyer_name.trim(),
        buyer_email: paymentData.buyer_email.trim(),
        buyer_phone: paymentData.buyer_phone.trim(),
        amount_paid: paymentData.amount_paid.trim(),
        payment_type: paymentData.payment_type,
        no_of_products: paymentData.no_of_products,
        status: paymentData.status,
        buyer: paymentData.buyer,
        product: paymentData.product,
        // Send date-only string (YYYY-MM-DD); backend stores it as a date field
        payment_date: paymentData.payment_date || null,
      };

      try {
        if (selectedOrder?.id) {
          await patchPartialPayment(selectedOrder.id, payload);
          setNotification("Payment updated successfully.");
          toast.success("Payment updated successfully.");
        } else {
          await postPartialPayment(payload);
          setNotification("Payment created successfully.");
          toast.success("Payment created successfully.");
        }
        closePopup();
        fetchOrders(currPage, perPage);
      } catch (error) {
        console.error("Error saving payment", error);
        setNotification("Unable to save payment. Please try again.");
        toast.error("Unable to save payment. Please try again.");
      }
    },
    [paymentData, selectedOrder, closePopup, fetchOrders, currPage, perPage]
  );

  const confirmDelete = useCallback((order) => {
    setOrderToDelete(order);
    setShowDeleteConfirm(true);
  }, []);

  const cancelDelete = useCallback(() => {
    setOrderToDelete(null);
    setShowDeleteConfirm(false);
  }, []);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!orderToDelete?.id) return;
    try {
      await deletePartialPayment(orderToDelete.id);
      setNotification("Payment deleted successfully.");
      toast.success("Payment deleted successfully.");
      setShowDeleteConfirm(false);
      setOrderToDelete(null);
      fetchOrders(currPage, perPage);
    } catch (error) {
      console.error("Error deleting payment", error);
      setNotification("Unable to delete payment. Please try again.");
      toast.error("Unable to delete payment. Please try again.");
    }
  }, [orderToDelete, fetchOrders, currPage, perPage]);

  const handleCopyPaymentId = useCallback((paymentId) => {
    const text = `Payment ID: ${paymentId}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(paymentId)
        .then(() => toast.success("Payment ID copied to clipboard"))
        .catch(() => {
          console.log("Clipboard fallback:", text);
          toast.info("Copied (fallback)");
        });
    } else {
      console.log("Clipboard fallback:", text);
      toast.info("Copied (fallback)");
    }
  }, []);

  const handleWhatsAppShare = useCallback((order) => {
    const message =
      `Order #${order.payment_id || order.id}\n` +
      `Buyer: ${order.buyer_name || "N/A"}\n` +
      `Amount: ${order.amount_paid || "N/A"}\n` +
      `Status: ${getStatusLabel(order.status, order.status_label)}\n` +
      `Products: ${order.product_details?.equip_name || "N/A"}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
    toast.info("Opening WhatsApp...");
  }, []);

  // ----- Render -----
  return (
    <main className="space-y-6 px-4 py-6 lg:px-8">
      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <section className="rounded-3xl bg-gradient-to-r from-slate-800 via-teal-700 to-slate-900 p-6 shadow-2xl shadow-teal-500/20 text-white">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-teal-200">
              Individual Coupons & Virtual Payments
            </p>
            <h1 className="mt-2 text-3xl font-semibold">
              Smooth admin order management
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-200/90">
              Manage individual coupons and create virtual payments with a fresh
              table layout, improved spacing, and modern controls.
            </p>
          </div>
          <button
            type="button"
            onClick={openNewPaymentModal}
            className="inline-flex items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-slate-900/10 transition hover:-translate-y-0.5 hover:bg-slate-100"
          >
            Create Coupons
          </button>
        </div>
      </section>

      {/* Notification Banner (kept for consistency) */}

      {/* Table */}
      <section className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-xl shadow-slate-900/5">
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-0 text-left">
            <thead className="bg-slate-50 text-sm text-slate-600">
              <tr>
                <th className="px-6 py-4 font-medium">#</th>
                <th className="px-6 py-4 font-medium">Payment ID</th>
                <th className="px-6 py-4 font-medium">Buyer</th>
                <th className="px-6 py-4 font-medium">Email</th>
                <th className="px-6 py-4 font-medium">Phone</th>
                <th className="px-6 py-4 font-medium">Discount Amount</th>
                {/* <th className="px-6 py-4 font-medium">Payment</th> */}
                <th className="px-6 py-4 font-medium">Coupon Validity</th>
                <th className="px-6 py-4 font-medium">Used</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Products</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => {
                const serial = (currPage - 1) * perPage + index + 1;
                return (
                  <tr
                    key={order.id || index}
                    className="border-t border-slate-200/80 transition hover:bg-slate-50"
                  >
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {serial}
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        <span>{order.payment_id || "-"}</span>
                        <button
                          type="button"
                          onClick={() =>
                            handleCopyPaymentId(order.payment_id || order.id)
                          }
                          className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Copy payment ID"
                        >
                          <FiCopy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {order.buyer_name}
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {order.buyer_email}
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {order.buyer_phone}
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {order.amount_paid}
                    </td>
                    {/* Payment type column commented out */}
                    {/* <td className="px-6 py-5 text-sm text-slate-700">
                      {getPaymentTypeLabel(order.payment_type)}
                    </td> */}
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {/* {order.payment_date
                        ? order.payment_date
                        : "-"} */}
                        Valid for 24hrs from creation
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {order.is_used ? "Yes" : "No"}
                    </td>
                    <td className="px-6 py-5">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[
                          getStatusLabel(order.status, order.status_label)
                        ] ?? "bg-slate-100 text-slate-700"
                          }`}
                      >
                        {getStatusLabel(order.status, order.status_label)}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-700">
                      {order.product_details?.equip_name || "-"}
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="inline-flex items-center justify-end gap-2">
                        {/* WhatsApp Share */}
                        <button
                          type="button"
                          onClick={() => openPaymentModal(order)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                          aria-label="Edit payment"
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleWhatsAppShare(order)}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                          aria-label="Share on WhatsApp"
                        >
                          <FiShare2 className="h-4 w-4" />
                        </button>
                        {/* Delete */}
                        <button
                          type="button"
                          onClick={() => confirmDelete(order)}
                          className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-red-500/10 transition hover:bg-red-700"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pagination */}
      <Pagination
        total={paging.total}
        perPage={perPage}
        setPerPage={setPerPage}
        currPage={currPage}
        setCurrPage={setCurrPage}
        prevPage={prevPage}
        tPage={paging.total_pages}
        updatePage={updatePage}
      />

      {/* Modal */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/20">
            <div className="flex items-center justify-between gap-4 border-b border-slate-200 pb-4">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">
                  {selectedOrder
                    ? "Edit Coupons "
                    : "Create Coupons "}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedOrder
                    ? "Update the selected coupon details."
                    : "Create a new coupon record."}
                </p>
              </div>
              <button
                type="button"
                onClick={closePopup}
                className="rounded-full bg-slate-100 p-2 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
              >
                ×
              </button>
            </div>

            <form className="mt-6 grid gap-4" onSubmit={submitVirtualPayment}>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Buyer Name
                  </label>
                  <input
                    type="text"
                    name="buyer_name"
                    value={paymentData.buyer_name}
                    onChange={handlePaymentChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Buyer Email
                  </label>
                  <input
                    type="email"
                    name="buyer_email"
                    value={paymentData.buyer_email}
                    onChange={handlePaymentChange}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 relative" ref={userDropdownRef}>
                  <label className="text-sm font-medium text-slate-700">
                    Buyer Phone
                  </label>
                  <input
                    type="text"
                    name="buyer_phone"
                    value={paymentData.buyer_phone}
                    onChange={(e) => handleBuyerSearchInput(e.target.value)}
                    onFocus={() => setShowUserDropdown(true)}
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none"
                    autoComplete="off"
                  />
                  {showUserDropdown && filteredUsers.length > 0 && (
                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl">
                      {filteredUsers.slice(0, 8).map((user) => (
                        <button
                          key={user.uid || user.id}
                          type="button"
                          onClick={() => handleSelectBuyer(user)}
                          className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-100"
                        >
                          <div className="font-semibold">
                            {user.name || user.username || "Unknown"}
                          </div>
                          <div className="text-xs text-slate-500">
                            {user.email}
                          </div>
                          <div className="text-xs text-slate-500">
                            {user.phone || user.mobile}
                          </div>
                        </button>
                      ))}
                      {filteredUsers.length > 8 && (
                        <div className="px-4 py-2 text-xs text-slate-400">
                          + {filteredUsers.length - 8} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2 relative" ref={productDropdownRef}>
                  <label className="text-sm font-medium text-slate-700">
                    Product
                  </label>
                  <input
                    type="text"
                    name="product_name"
                    value={productQuery}
                    onChange={(e) => handleProductInput(e.target.value)}
                    onFocus={() => setShowProductDropdown(true)}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                    autoComplete="off"
                  />
                  {showProductDropdown && productQuery && (
                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl">
                      {productSuggestions.length > 0 ? (
                        productSuggestions.slice(0, 8).map((product) => (
                          <button
                            key={product.id || product.product_id || product.uid}
                            type="button"
                            onClick={() => handleSelectProduct(product)}
                            className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-slate-100"
                          >
                            <div className="font-semibold">
                              {product.equip_name ||
                                product.name ||
                                product.title ||
                                "Product"}
                            </div>
                            {product.brand && (
                              <div className="text-xs text-slate-500">
                                {product.brand}
                              </div>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-500">
                          No products found.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Discount Amount
                  </label>
                  <input
                    type="text"
                    name="amount_paid"
                    value={paymentData.amount_paid}
                    onChange={handlePaymentChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    No. of Products
                  </label>
                  <input
                    type="number"
                    name="no_of_products"
                    value={paymentData.no_of_products}
                    onChange={handlePaymentChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  />
                </div>
              </div>

              {/* <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Payment Type
                  </label>
                  <select
                    name="payment_type"
                    value={paymentData.payment_type}
                    onChange={handlePaymentChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  >
                    {paymentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={paymentData.status}
                    onChange={handlePaymentChange}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div> */}

              {/* <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Date for Validity(expires after 24hrs)
                </label>
                <input
                  type="date"
                  name="payment_date"
                  value={paymentData.payment_date}
                  onChange={handlePaymentChange}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                />
              </div> */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Additional Notes
                </label>
                <textarea
                  name="others"
                  value={paymentData.others}
                  onChange={handlePaymentChange}
                  rows={2}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
                />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closePopup}
                  className="rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 transition hover:bg-slate-800"
                >
                  {selectedOrder ? "Update Payment" : "Save Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/20">
            <h3 className="text-xl font-semibold text-slate-900">
              Delete Confirmation
            </h3>
            <p className="mt-3 text-sm text-slate-600">
              Are you sure you want to delete payment{" "}
              <span className="font-semibold">
                {orderToDelete?.payment_id || orderToDelete?.id}
              </span>
              ? This cannot be undone.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={cancelDelete}
                className="rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirmed}
                className="rounded-full bg-red-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/10 transition hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default OffLineOrders;